'use client';
import {useEffect,useRef,useState} from 'react';
import {PublicShell,SourceSurface} from './DownloadPage';
import {usePublicCopy} from './content/navigation';
import {skillInstallers,skillCategories as categories,projectCategories,filterSkills,skillDownload,type SkillInstaller} from './content/skills';
// 布局与动作源于固定 mac SkillsPage.jsx；使用原21包，下载核对摘要，不执行安装器。
function SkillsContent() {
  const {t:translate}=usePublicCopy();const t=(value:string,params?:Record<string,string|number>)=>Object.entries(params??{}).reduce((text,[key,part])=>text.replaceAll('{'+key+'}',String(part)),translate(value));
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("全部");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [manualCopy, setManualCopy] = useState("");
  const actionRef = useRef(false);
  const mountedRef = useRef(true);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const copyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!manualCopy) return;
    dialogRef.current?.showModal();
    copyRef.current?.focus();
    copyRef.current?.select();
  }, [manualCopy]);

  const download = async (item:SkillInstaller) => {
    if (actionRef.current) return;
    actionRef.current = true;
    setBusy(item.path);
    setNotice("");
    setError("");
    try {
      const asset=skillDownload(item);const url=asset?'/mac/skills/'+asset.source.split('/').pop():null;
      if (!url) throw new Error("安装包暂不可用，请稍后重试。");
      const response = await fetch(url, { credentials: "omit", redirect: "error" });
      if (!response.ok || response.headers.get("content-type")?.includes("text/html")) throw new Error("安装包下载未完成，请重试。");
      const bytes=await response.arrayBuffer();
      if(bytes.byteLength!==asset.bytes||Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256",bytes))).map(value=>value.toString(16).padStart(2,"0")).join("")!==asset.sha256)throw new Error("安装包内容不完整，请重试。");
      const content = new TextDecoder().decode(bytes);
      if (!content.trimStart().startsWith("# ")) throw new Error("安装包内容不完整，请重试。");
      if (!mountedRef.current) return;
      const downloadUrl = URL.createObjectURL(new Blob([bytes], { type: "text/markdown;charset=utf-8" }));
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = item.filename;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 5000);
      setNotice("下载已开始。复制安装指令后，将文件和指令一起发给 Codex。");
    } catch (cause) {
      if (mountedRef.current) setError(cause instanceof Error ? cause.message : "安装包下载未完成，请重试。");
    } finally {
      actionRef.current = false;
      if (mountedRef.current) setBusy("");
    }
  };

  const copy = async (item:SkillInstaller) => {
    if (actionRef.current) return;
    actionRef.current = true;
    setBusy(`copy:${item.path}`);
    setError("");
    setNotice("");
    try {
      await navigator.clipboard.writeText(item.prompt);
      if (mountedRef.current) setNotice("安装指令已复制。将指令和下载的安装包一起发给 Codex。");
    } catch {
      if (mountedRef.current) setManualCopy(item.prompt);
    } finally {
      actionRef.current = false;
      if (mountedRef.current) setBusy("");
    }
  };

  const actions = (item:SkillInstaller, bundle = false) => <div className="web-skill-actions">
    <button className="web-skill-primary" disabled={Boolean(busy)} onClick={() => void download(item)} type="button" aria-label={t(bundle ? "下载集中安装包：{title}" : "下载安装包：{title}", { title: t(item.title) })}>
      {t(busy === item.path ? "正在准备…" : bundle ? "下载全部" : "下载安装包")}
    </button>
    <button disabled={Boolean(busy)} onClick={() => void copy(item)} type="button" aria-label={t("复制安装指令：{title}", { title: t(item.title) })}>
      {t(busy === `copy:${item.path}` ? "正在复制…" : "复制安装指令")}
    </button>
  </div>;

  const filtered=filterSkills(query,category);

  return <section className="section web-skills-page" id="skills" aria-labelledby="web-skills-title">
    <header className="web-skills-heading">
      <div>
        <span className="web-skills-eyebrow">{t("SKILLS · 技能")}</span>
        <h1 id="web-skills-title">{t("给 Codex 加上实用技能")}</h1>
        <p>{t("20 项创作、办公与自动化技能，按需安装。")}</p>
      </div>
      <div className="web-skills-availability"><strong>{t("安装包免费下载")}</strong><small>{t("适用于 macOS Codex 桌面端")}</small></div>
    </header>

    <div className="web-skills-start">
      <div className="web-skills-start-copy">
        <h2>{t("集中安装 20 项技能")}</h2>
        <p>{t("每项技能独立建项目，配好使用说明。")}</p>
        <ol className="web-skills-steps"><li>{t("下载安装包")}</li><li>{t("复制安装指令")}</li><li>{t("将文件和指令一起发给 Codex")}</li></ol>
      </div>
      {actions(skillInstallers.bundle, true)}
      <div className="web-skills-start-meta">
        <p className="web-skills-next">{t("安装后，从“使用说明｜先从这里开始”继续。")}<a href="/download">{t("获取 Codex 助手")}</a></p>
        <p className="web-skills-boundary">{t("生图等第三方服务需另行连接，使用费用另计。")} {t("安装说明面向 macOS Codex 桌面端；请先确认客户端可用、系统符合其要求，并准备 Python 3，再按包内指令检查所需依赖。" )}</p>
      </div>
    </div>

    <div className="web-skills-toolbar">
      <div className="web-skills-categories" aria-label={t("技能分类")}>{categories.map(value => <button key={value} type="button" aria-pressed={category === value} onClick={() => setCategory(value)}>{t(value)}</button>)}</div>
      <label className="web-skills-search"><span>{t("搜索技能")}</span><input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder={t("文案、视频、Excel、飞书")} /></label>
    </div>
    <div className="web-skills-results" aria-live="polite"><h2>{t("按需安装")}</h2><span>{t("{count} 个项目", { count: filtered.length })}</span></div>
    <div className="web-skills-feedback" aria-live="polite">{notice && <p role="status">{t(notice)}</p>}{error && <p className="web-skills-error" role="alert">{t(error)}</p>}</div>
    <div className="web-skills-grid">{filtered.map(item => <article className="web-skill-item" key={item.number}>
      <header><span className="web-skill-number">{item.number}</span><h3>{t(item.title)}</h3><span className="web-skill-category">{t(projectCategories[Number(item.number) - 1])}</span></header>
      <p>{t(item.description)}</p>
      {actions(item)}
    </article>)}</div>
    {!filtered.length && <div className="web-skills-empty"><p>{t("没有找到相关技能，换个关键词试试。")}</p><button type="button" onClick={() => { setQuery(""); setCategory("全部"); }}>{t("查看全部技能")}</button></div>}
    <dialog className="web-skills-copy" ref={dialogRef} onClose={() => setManualCopy("")}>
      <h2>{t("复制安装指令")}</h2><p>{t("请选择并复制下方指令，与安装包一起发给 Codex。")}</p>
      <textarea ref={copyRef} value={manualCopy} readOnly aria-label={t("安装指令")} />
      <form method="dialog"><button type="submit">{t("完成")}</button></form>
    </dialog>
  </section>;
}

export function SkillsPage(){return <PublicShell><SourceSurface page="skills"><SkillsContent/></SourceSurface></PublicShell>}
